import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/App";
import { motion } from "framer-motion";
import { Music, ExternalLink, BadgeCheck, Globe, ArrowRight, Mail } from "lucide-react";
import { FaTelegram, FaInstagram, FaVk, FaTiktok, FaTwitter, FaGlobe, FaSpotify, FaApple, FaYoutube, FaSoundcloud, FaLink, FaYandex, FaAmazon, FaItunes, FaGoogle, FaNapster, FaBandcamp } from "react-icons/fa";
import { SiTidal, SiPandora, SiAudiomack } from "react-icons/si";
import MuslinkLogo from "@/components/MuslinkLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import { getMainDomainUrl } from "@/utils/subdomain";
import PublicPage from "@/pages/PublicPage";

// Custom SVG icons for platforms
const DeezerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.01 11.75H0V15h6.01v-3.25zM6.01 7.25H0v3.25h6.01V7.25zM6.01 16.25H0v3.25h6.01v-3.25zM12.005 11.75H6.01V15h5.995v-3.25zM12.005 16.25H6.01v3.25h5.995v-3.25zM17.995 11.75H12V15h5.995v-3.25zM17.995 16.25H12v3.25h5.995v-3.25zM17.995 7.25H12v3.25h5.995V7.25zM24 11.75h-6.005V15H24v-3.25zM24 16.25h-6.005v3.25H24v-3.25zM24 7.25h-6.005v3.25H24V7.25zM24 2.75h-6.005V6H24V2.75z"/>
  </svg>
);

const YouTubeMusicIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor"/>
    <polygon points="10,8 16,12 10,16" fill="white"/>
  </svg>
);

const GooglePlayIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

const PandoraIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15v-4H8v-2h2V9c0-1.71 1.39-3 3.1-3H16v2h-2.9c-.59 0-1.1.51-1.1 1v2h4v2h-4v4h-2z"/>
  </svg>
);

const AudiusIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor"/>
    <path d="M8 16V8l8 4-8 4z" fill="white"/>
  </svg>
);

const AnghamiIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor"/>
    <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="white"/>
  </svg>
);

const BoomplayIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor"/>
    <path d="M10 8v8l6-4-6-4z" fill="white"/>
  </svg>
);

const SpinrillaIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor"/>
    <circle cx="12" cy="12" r="4" fill="white"/>
  </svg>
);

const ZvukIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M8 8l8 4-8 4V8z" fill="currentColor"/>
  </svg>
);

const MtsIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <rect x="3" y="6" width="18" height="12" rx="2" fill="currentColor"/>
    <text x="12" y="14" fontSize="6" fill="white" textAnchor="middle" fontWeight="bold">MTC</text>
  </svg>
);

// Platform icons/colors (same as PublicPage)
const PLATFORMS = {
  spotify: { color: "#1DB954", name: "Spotify", icon: FaSpotify },
  appleMusic: { color: "#FA233B", name: "Apple Music", icon: FaApple },
  itunes: { color: "#EA4CC0", name: "iTunes", icon: FaItunes },
  youtube: { color: "#FF0000", name: "YouTube", icon: FaYoutube },
  youtubeMusic: { color: "#FF0000", name: "YouTube Music", icon: YouTubeMusicIcon },
  yandex: { color: "#FFCC00", name: "Яндекс Музыка", icon: FaYandex },
  vk: { color: "#4C75A3", name: "VK Музыка", icon: FaVk },
  deezer: { color: "#A238FF", name: "Deezer", icon: DeezerIcon },
  tidal: { color: "#000000", name: "Tidal", icon: SiTidal },
  amazonMusic: { color: "#FF9900", name: "Amazon Music", icon: FaAmazon },
  amazonStore: { color: "#FF9900", name: "Amazon Store", icon: FaAmazon },
  soundcloud: { color: "#FF5500", name: "SoundCloud", icon: FaSoundcloud },
  pandora: { color: "#005483", name: "Pandora", icon: PandoraIcon },
  napster: { color: "#000000", name: "Napster", icon: FaNapster },
  audiomack: { color: "#FFA200", name: "Audiomack", icon: SiAudiomack },
  audius: { color: "#CC0FE0", name: "Audius", icon: AudiusIcon },
  anghami: { color: "#6C3694", name: "Anghami", icon: AnghamiIcon },
  boomplay: { color: "#E11B22", name: "Boomplay", icon: BoomplayIcon },
  spinrilla: { color: "#121212", name: "Spinrilla", icon: SpinrillaIcon },
  bandcamp: { color: "#629AA9", name: "Bandcamp", icon: FaBandcamp },
  google: { color: "#4285F4", name: "Google", icon: FaGoogle },
  googleStore: { color: "#34A853", name: "Google Store", icon: GooglePlayIcon },
  zvuk: { color: "#6B4EFF", name: "Звук", icon: ZvukIcon },
  mts: { color: "#E30611", name: "МТС Музыка", icon: MtsIcon },
  // Legacy mappings
  apple: { color: "#FA233B", name: "Apple Music", icon: FaApple },
  amazon: { color: "#FF9900", name: "Amazon Music", icon: FaAmazon },
  apple_music: { color: "#FA243C", name: "Apple Music", icon: FaApple },
  youtube_music: { color: "#FF0000", name: "YouTube Music", icon: YouTubeMusicIcon },
  amazon_music: { color: "#FF9900", name: "Amazon Music", icon: FaAmazon },
  yandex_music: { color: "#FFCC00", name: "Яндекс Музыка", icon: FaYandex },
  vk_music: { color: "#0077FF", name: "VK Музыка", icon: FaVk },
  custom: { color: "#8B5CF6", name: "Ссылка", icon: FaLink }
};

// Social platforms for contact info
const SOCIAL_PLATFORMS = {
  telegram: { name: "Telegram", icon: FaTelegram, color: "#229ED9", urlPrefix: "https://t.me/" },
  instagram: { name: "Instagram", icon: FaInstagram, color: "#E4405F", urlPrefix: "https://instagram.com/" },
  vk: { name: "VKontakte", icon: FaVk, color: "#4C75A3", urlPrefix: "https://vk.com/" },
  tiktok: { name: "TikTok", icon: FaTiktok, color: "#000000", urlPrefix: "https://tiktok.com/@" },
  twitter: { name: "X", icon: FaTwitter, color: "#1DA1F2", urlPrefix: "https://twitter.com/" },
  website: { name: "Сайт", icon: FaGlobe, color: "#6B7280", urlPrefix: "" },
};

// Helper to format social link URL
const formatSocialUrl = (platform, value) => {
  if (!value) return null;
  
  // If already a URL, return as is
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  
  const platformInfo = SOCIAL_PLATFORMS[platform];
  if (!platformInfo) return value;
  
  // Remove @ if present
  const cleanValue = value.startsWith("@") ? value.substring(1) : value;
  
  if (platform === "website") {
    return `https://${cleanValue}`;
  }
  
  return `${platformInfo.urlPrefix}${cleanValue}`;
};

function BrandFooter({ light = false }) {
  return (
    <div className="flex justify-center pt-10 pb-4">
      <a
        href="https://mus.link/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Mus.Link"
        className="inline-flex items-center justify-center transition-opacity hover:opacity-80"
      >
        <MuslinkLogo height={16} theme={light ? "light" : "dark"} className={light ? "opacity-55" : "opacity-45"} />
      </a>
    </div>
  );
}

const SUBDOMAIN_COPY = {
  en: {
    discography: "Discography",
    releaseCount: (count) => `${count} ${count === 1 ? "release" : "releases"}`,
    chartRank: (rank) => `#${rank} in Chart`,
  },
  ru: {
    discography: "Дискография",
    releaseCount: (count) => {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return `${count} релиз`;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} релиза`;
      return `${count} релизов`;
    },
    chartRank: (rank) => `#${rank} в чарте`,
  },
  es: {
    discography: "Discografía",
    releaseCount: (count) => `${count} ${count === 1 ? "lanzamiento" : "lanzamientos"}`,
    chartRank: (rank) => `#${rank} en el chart`,
  },
};

function getSubdomainCopy(language) {
  return SUBDOMAIN_COPY[language] || SUBDOMAIN_COPY.en;
}

export default function SubdomainPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [subdomain, setSubdomain] = useState("");

  useEffect(() => {
    // Get subdomain from hostname
    const host = window.location.hostname.toLowerCase();
    const mainDomain = 'mus.link';
    let sub = "";
    
    if (host.endsWith(`.${mainDomain}`)) {
      sub = host.replace(`.${mainDomain}`, "");
    } else {
      // For development - check URL params
      const params = new URLSearchParams(window.location.search);
      sub = params.get('subdomain') || "";
    }
    
    setSubdomain(sub);
    
    if (sub) {
      fetchData(sub, slug);
    } else {
      setError("Поддомен не найден");
      setLoading(false);
    }
  }, [slug]);

  const fetchData = async (sub, pageSlug) => {
    try {
      const endpoint = pageSlug 
        ? `/subdomain-page?slug=${pageSlug}`
        : '/subdomain-page';
      
      const response = await api.get(endpoint, {
        headers: { 'X-Subdomain': sub }
      });
      
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Страница не найдена");
      } else if (err.response?.status === 410) {
        setError("Домен неактивен");
      } else {
        setError("Ошибка загрузки");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
          <p className="text-muted-foreground">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <div className="text-center">
          <Globe className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
          <p className="text-muted-foreground mb-6">
            {subdomain ? `${subdomain}.mus.link` : "Неизвестный домен"}
          </p>
          <a href="https://mus.link" className="text-primary hover:underline">
            Перейти на главную →
          </a>
        </div>
      </div>
    );
  }

  // If we have a page (slug provided), show the page
  if (data?.links) {
    return <PageView page={data} subdomain={subdomain} />;
  }

  // Otherwise show pages list for this subdomain
  return <PagesListView data={data} subdomain={subdomain} />;
}

// Component to display a single page.
// Reuses the main PublicPage rendering so the subdomain looks identical to
// mus.link/<slug>. The page data is fetched here and passed in via props.
function PageView({ page, subdomain }) {
  return (
    <div className="relative">
      <PublicPage pageData={page} hideNavigation />
    </div>
  );
}

// Component to display pages list
function PagesListView({ data, subdomain }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { language } = useLanguage();
  // Render the artist's profile page in the artist's chosen language
  const copy = getSubdomainCopy(data?.owner_language || language);
  
  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/') ? `${API_URL}${url}` : url;
  };

  const hasContacts = data.contact_email || (data.social_links && Object.values(data.social_links).some(v => v));

  const backgroundUrl = getImageUrl(data.background_url);
  const avatarUrl = getImageUrl(data.avatar_url);
  const pages = [...(data.pages || [])].sort((a, b) => {
    if (a.chart_rank && b.chart_rank) return a.chart_rank - b.chart_rank;
    if (a.chart_rank) return -1;
    if (b.chart_rank) return 1;
    return 0;
  });
  const heroCover = getImageUrl(pages.find(page => page.cover_image)?.cover_image);
  const displayName = data.artist_name || data.username || subdomain;
  const hasSocials = data.social_links && Object.values(data.social_links).some(Boolean);
  const topChartRelease = data.top_chart_release;

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#070809] text-white">
      <div
        className="fixed inset-0 bg-cover bg-center opacity-35"
        style={{
          backgroundImage: backgroundUrl || heroCover ? `url(${backgroundUrl || heroCover})` : undefined,
          filter: backgroundUrl || heroCover ? "blur(34px) saturate(1.12)" : undefined,
          transform: backgroundUrl || heroCover ? "scale(1.08)" : undefined,
        }}
      />
      <div className="fixed inset-0 bg-[linear-gradient(180deg,rgba(217,70,239,0.14),rgba(12,14,16,0.72)_28%,#070809_68%)]" />
      <div className="fixed inset-x-0 top-0 h-64 bg-gradient-to-b from-white/8 to-transparent" />

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-col justify-end pb-7 pt-14 sm:pt-16 lg:pt-20"
        >
          <div className="max-w-3xl">
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 sm:gap-5">
                <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-3xl border border-white/15 bg-white/10 shadow-2xl shadow-primary/20 sm:h-32 sm:w-32">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary via-fuchsia-500 to-indigo-500">
                      <span className="text-4xl font-bold text-white">{displayName.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="min-w-0 break-words pb-2 text-4xl font-semibold leading-[1.18] sm:text-6xl lg:text-7xl">
                    {displayName}
                    {data.verified && <BadgeCheck className="mb-1 ml-2 inline-block h-6 w-6 text-white sm:mb-2 sm:h-7 sm:w-7" />}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 sm:text-base">
                    {data.profile_description || "Музыка, релизы и актуальные ссылки в одном профиле."}
                  </p>
                  {topChartRelease && (
                    <a
                      href={getMainDomainUrl("/chart")}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/8 px-3 py-1.5 text-xs text-zinc-200 backdrop-blur-md transition-all hover:border-white/50 hover:bg-white/12"
                    >
                      <span className="max-w-[220px] truncate sm:max-w-xs">{topChartRelease.release_title}</span>
                      <span className="font-semibold text-white">{copy.chartRank(topChartRelease.rank)}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.header>

        <section className="pb-8">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold sm:text-2xl">{copy.discography}</h2>
              <p className="mt-1 text-sm text-zinc-400">{copy.releaseCount(pages.length)}</p>
            </div>
          </div>

          {pages.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {pages.map((page, idx) => {
                const cover = getImageUrl(page.cover_image);
                return (
                  <motion.div
                    key={page.id}
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + idx * 0.055, duration: 0.42, ease: "easeOut" }}
                  >
                    <Link to={`/${page.slug}`} className="group block">
                      <article className="rounded-2xl border border-white/10 bg-white/8 p-3 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/60 hover:bg-white/12 hover:shadow-2xl hover:shadow-white/10">
                        <div className="relative aspect-square overflow-hidden rounded-xl bg-zinc-900">
                          {cover ? (
                            <img
                              src={cover}
                              alt={page.title}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Music className="h-12 w-12 text-zinc-600" />
                            </div>
                          )}
                          {page.chart_rank && (
                            <div className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-md">
                              {copy.chartRank(page.chart_rank)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-start justify-between gap-3 px-1 pb-1 pt-4">
                          <div className="min-w-0">
                            <h3 className="truncate text-base font-semibold text-white">{page.release_title || page.title}</h3>
                            <p className="mt-1 truncate text-sm text-zinc-400">{page.artist_name || displayName}</p>
                          </div>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white opacity-70 backdrop-blur-md transition-all duration-300 group-hover:opacity-100">
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </article>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-white/6 px-6 py-16 text-center backdrop-blur-xl">
              <Music className="mx-auto mb-4 h-14 w-14 text-zinc-600" />
              <p className="text-zinc-400">Нет опубликованных релизов</p>
            </div>
          )}
        </section>

        {hasContacts && (
          <motion.section
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24, duration: 0.45 }}
            className="mt-auto border-t border-white/10 py-8"
          >
            <div className="flex flex-col items-center gap-5">
              {data.contact_email && (
                <a
                  href={`mailto:${data.contact_email}`}
                  className="group inline-flex w-full max-w-md items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-zinc-200 backdrop-blur-xl transition-all hover:border-white/60 hover:bg-white/12"
                >
                  <Mail className="h-4 w-4 text-white" />
                  <span className="truncate group-hover:text-white">{data.contact_email}</span>
                </a>
              )}

              {hasSocials && (
                <div className="flex flex-wrap justify-center gap-3">
                  {Object.entries(data.social_links).map(([platform, value]) => {
                    if (!value) return null;

                    const platformInfo = SOCIAL_PLATFORMS[platform];
                    if (!platformInfo) return null;

                    const Icon = platformInfo.icon;
                    const url = formatSocialUrl(platform, value);

                    return (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/8 backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/12"
                        title={platformInfo.name}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.section>
        )}

        <BrandFooter />
      </main>
    </div>
  );
}
